import { PublicationShortname } from "@src/app/auth/serviceIdentifier";
import Cookies from "js-cookie";

export const COOKIE_MYSPH_SID = "mysph-sid";
export const COOKIE_MYSPH_USER = "mysph-user";
export const COOKIE_SPH_GROUP = "mysph-group";

export type TViewerContext = {
  authStatus?: string;
  userProfile?: {
    login_id?: string;
    user_email?: string;
    visitor_id?: string;
    first_name?: string;
    last_name?: string;
    service_flags?: string[];
    concurrent_config?: string[];
  };
};

export type TVisitorContext = {
  visitorCategory: "Anonymous" | "Subscriber" | "Registered" | "nlbsubscriber";
  visitorId: string;
  svc: string;
};

export const DEFAULT_VISITOR_CONTEXT: TVisitorContext = {
  visitorCategory: "Anonymous",
  visitorId: "",
  svc: "",
};

/**
 * Fetches the viewer context from the server. Always hits the API so the
 * returned visitor_id matches the active mysph-sid — never cache the result
 * client-side, since the same browser can hand off to a different mySPH
 * session (silent SSO, shared device) without going through our logout.
 */
export async function fetchVisitorContext(
  pubsId: PublicationShortname,
): Promise<TVisitorContext> {
  try {
    const response = await fetch(`/${pubsId}/api/viewerContext`, {
      method: "GET",
      credentials: "include",
    });

    if (response.ok) {
      const data = (await response.json()) as TViewerContext;

      // CONCURRENCY_EXCEEDED is still an authenticated subscriber with valid
      // service_flags — they have only tripped the concurrent-session limit.
      // Extract their svc so entitlement is recognised (e.g. the Home preview
      // CTA is NOT shown to a real subscriber). Without this they fall through
      // to DEFAULT_VISITOR_CONTEXT (svc="") and isEntitledForPub() wrongly
      // returns false. The concurrency limit itself is still handled by the
      // existing flow (middleware -> /{pub}#concurrency -> lb.concurrencyExceeded
      // when they open the reader).
      if (
        data.authStatus === "AUTHENTICATED" ||
        data.authStatus === "CONCURRENCY_EXCEEDED"
      ) {
        const sphGroup = Cookies.get(COOKIE_SPH_GROUP);
        const visitorCategory =
          sphGroup === "corp.nlb" ? "nlbsubscriber" : "Subscriber";

        return {
          visitorCategory,
          visitorId: data.userProfile?.visitor_id ?? "",
          svc: mergeServiceFlagsWithDelimitor(
            data.userProfile?.service_flags ?? [],
          ),
        };
      }
    }

    return DEFAULT_VISITOR_CONTEXT;
  } catch (error: unknown) {
    console.error(error);

    return DEFAULT_VISITOR_CONTEXT;
  }
}

/**
 * Server-validated auth check for the current session. Unlike the client-side
 * Lightbox `isAuthenticated` — which only reflects `mysph-sid` cookie presence
 * and stays true after a silent token expiry — this validates the session
 * against MySPH via the viewerContext endpoint.
 */
export async function isSessionAuthenticated(
  pubsId: PublicationShortname,
): Promise<boolean> {
  try {
    const response = await fetch(`/${pubsId}/api/viewerContext`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      return false;
    }

    const data = (await response.json()) as TViewerContext;

    return data.authStatus === "AUTHENTICATED";
  } catch (error: unknown) {
    console.error(error);

    return false;
  }
}

export function clearLoginSession(pubId: string): void {
  Cookies.remove(COOKIE_MYSPH_SID, { path: `/${pubId}` });
  Cookies.remove(COOKIE_MYSPH_USER, { path: `/${pubId}` });
  Cookies.remove(COOKIE_SPH_GROUP, { path: `/${pubId}` });
}

/**
 * Clears the session by calling the server-side API endpoint which then calls
 * MySPH API to logout the session.
 *
 * The caller is responsible for providing the session ID (e.g. from cookie).
 */
export async function clearSessionID(
  sessionID: string | undefined,
): Promise<void> {
  try {
    const url = `/_plat/api/v1/sessions/clear`;
    const headers: HeadersInit = {};

    if (sessionID) {
      headers["x-session-id"] = sessionID;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
    });

    if (response.ok) {
      await response.json().catch(() => ({}));
    } else {
      const errorText = await response.text();
      console.error("clearSessionID - Failed to clear session:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
    }
  } catch (error: unknown) {
    console.error("clearSessionID - Error when clearing session:", error);
    // Don't throw - allow logout to proceed even if this fails
  }
}

/**
 *
 * @param serviceFlags Service Flags fields returned by mySPH in array of strings
 * @returns {string} merged service flags using delimitor "|"
 */
function mergeServiceFlagsWithDelimitor(serviceFlags: string[]): string {
  return serviceFlags.join("|");
}
